import { h } from '@atomify/jsx';
import { Image, ImageInterface } from '@atoms/image';
import { BPDFunctionalSvg } from '@atoms/index';
import { Breakpoint } from '@utilities/media-query/media-query.types';

interface SliderContainerProps {
    autoSlide?: boolean;
    autoSlideTimeout?: number;
    sliderIfNotGreaterThan?: Breakpoint;
    slidesToScroll?: number;
    classes?: string;
    title?: string;
    images?: ImageInterface[];
    children?: NodeList;
}

interface SliderCellProps {
    classes?: string;
    children?: NodeList;
}

const CELL_CONTAINER_HOOK = 'js-hook-slider-progress-cell-container';
const CELL_HOOK = 'js-hook-slider-progress-cell';

const SliderProgressContainer = (props: SliderContainerProps) => {
    const classList = {
        ['c-slider-progress']: true,
        ...(props.classes && { [props.classes]: true }),
        ...(props.sliderIfNotGreaterThan && {
            [`slider-progress--${props.sliderIfNotGreaterThan}-up-grid`]: true,
        }),
    };

    const Images = () => props.images?.map((image) => <SliderProgressImage {...image} />);

    return (
        <bpd-slider-progress
            auto-slide={props.autoSlide}
            auto-slide-timeout={props.autoSlideTimeout}
            slider-if-not-greater-than={props.sliderIfNotGreaterThan}
            slides-to-scroll={props.slidesToScroll || 1}>
            <div class={classList}>
                {props.title && (
                    <div class="slider-progress__header">
                        <h4 class="slider-progress__title">{props.title}</h4>
                    </div>
                )}

                <div class="slider-progress__slider-viewport">
                    <div class="slider-progress__slider-viewport-inner">
                        <div class="slider-progress__container" js-hook-slider-progress>
                            <div
                                class="slider-progress__slider"
                                {...{ [CELL_CONTAINER_HOOK]: true }}>
                                {props.images?.length ? <Images /> : props.children}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="slider-progress__footer" js-hook-slider-progress-footer>
                    <div class="slider-progress__progress-container">
                        <div class="slider-progress__progress" js-hook-slider-progress-progress />
                    </div>
                    <div class="slider-progress__controls">
                        <button
                            class="slider-progress__control slider-progress__control--previous"
                            aria-label="Go to previous slide"
                            js-hook-slider-progress-previous>
                            <BPDFunctionalSvg
                                name="icons-arrow-slim-left"
                                attr={{ ['class']: 'slider-progress__control-icon' }}
                            />
                        </button>
                        <button
                            class="slider-progress__control slider-progress__control--next"
                            aria-label="Go to next slide"
                            js-hook-slider-progress-next>
                            <BPDFunctionalSvg
                                name="icons-arrow-slim-right"
                                attr={{ ['class']: 'slider-progress__control-icon' }}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </bpd-slider-progress>
    );
};

const SliderProgressImage = (image: ImageInterface) => (
    <div class="slider-progress__cell" {...{ [`${CELL_HOOK}`]: true }}>
        <div class="slider-progress__cell-inner slider-progress__cell-inner--image">
            <Image
                {...image}
                classes="slider-progress__image"
                preload={{ width: 16, height: 9 }}
                settings={{
                    '640w': { width: 640, height: 360 },
                    '1280w': { width: 1280, height: 800 },
                    '1920w': { width: 1920, height: 1080 },
                }}
            />
        </div>
    </div>
);

const SliderProgressCell = ({ children, classes }: SliderCellProps) => {
    const classList = {
        ['slider-progress__cell']: true,
        ...(classes && { [classes]: true }),
    };

    return (
        <div class={classList} {...{ [`${CELL_HOOK}`]: true }}>
            <div class="slider-progress__cell-inner slider-progress__cell-inner--align-left">
                {children}
            </div>
        </div>
    );
};

export {
    CELL_CONTAINER_HOOK as SLIDER_PROGRESS_CELL_CONTAINER,
    CELL_HOOK as SLIDER_PROGRESS_CELL,
    SliderProgressContainer,
    SliderProgressCell,
};
